import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@apollo/orbit';
import { Input } from '@apollo/space-kit/Input';
import { List } from '@apollo/space-kit/List';
import { ListItem } from '@apollo/space-kit/ListItem';
import classnames from 'classnames';
import Downshift from 'downshift';
import React from 'react';

const suggestions = [
  'WWW-Authenticate',
  'Authorization',
  'Cache-Control',
  'Connection',
  'Keep-Alive',
  'Access-Control-Allow-Origin',
  'Access-Control-Allow-Credentials',
  'Access-Control-Allow-Headers',
  'Access-Control-Allow-Methods',
  'Access-Control-Expose-Headers',
  'Access-Control-Max-Age',
  'Access-Control-Request-Headers',
  'Access-Control-Request-Method',
  'apollo-federation-include-trace',
  'apollographql-client-name',
  'apollographql-client-version',
];

export const HeaderSuggestionInput = ({
  className,
  inputValue,
  checked = true,
  disabled = false,
  onInputValueChange,
  changeFocus,
}: {
  inputValue: string;
  onInputValueChange?: (value: string) => void;
  changeFocus: () => void;
  className: string;
  checked?: boolean;
  disabled?: boolean;
}) => {
  return (
    <Downshift
      inputValue={inputValue}
      onInputValueChange={onInputValueChange}
      onSelect={changeFocus}
    >
      {({
        getRootProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        highlightedIndex,
        setState,
        selectHighlightedItem,
      }) => {
        const inputProps = getInputProps({
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
              selectHighlightedItem();
            }
          },
        });
        const inputRef: React.Ref<HTMLInputElement> | undefined =
          typeof inputProps.ref === 'string' ? undefined : inputProps.ref;

        const filteredSuggestions: string[] = suggestions.filter(
          (suggestion) =>
            !inputValue ||
            (suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
              suggestion !== inputValue),
        );

        return (
          <div
            className={classnames('inline-block w-full', className)}
            {...getRootProps(undefined, { suppressRefError: true })}
          >
            <Popover
              placement="top-start"
              size="sm"
              // use this component in controlled mode since downshift controls the open state
              isOpen={true}
              autoFocus={false}
              closeOnBlur={true}
            >
              <PopoverTrigger>
                {/* TODO: before removing '@apollo/space-kit/Input' we should figure out a better component to use for this that supports autocomplete */}
                <Input
                  crossOrigin={false}
                  className={classnames(
                    'text-body h-10 w-full flex-1 rounded-lg border border-primary bg-input p-4 px-2 placeholder:text-placeholder hover:border-hover hover:text-placeholder focus:border-focused focus:bg-input focus:text-primary',
                    checked ? 'text-primary' : '',
                    disabled &&
                      'border-disabled bg-disabled text-disabled opacity-100 placeholder:text-disabled hover:border-disabled',
                  )}
                  {...inputProps}
                  ref={inputRef}
                  disabled={disabled}
                  autoComplete="off"
                  type="text"
                  name="key"
                  onFocus={() => setState({ isOpen: true })}
                  onBlur={() => setState({ isOpen: false })}
                  placeholder="header key"
                  size="standard"
                />
              </PopoverTrigger>
              {isOpen &&
                /* do not show empty popover when no suggestions match */
                filteredSuggestions.length > 0 && (
                  <PopoverContent>
                    <PopoverBody className="p-0">
                      <List
                        {...getMenuProps()}
                        style={{ maxHeight: 200 }}
                        className="overflow-auto rounded-md bg-white py-2 text-black shadow"
                      >
                        {filteredSuggestions.map((suggestion, index) => (
                          <ListItem
                            key={suggestion}
                            {...getItemProps({
                              key: suggestion,
                              index,
                              item: suggestion,
                            })}
                            className="text-sm text-black"
                            highlighted={highlightedIndex === index}
                          >
                            {suggestion}
                          </ListItem>
                        ))}
                      </List>
                    </PopoverBody>
                  </PopoverContent>
                )}
            </Popover>
          </div>
        );
      }}
    </Downshift>
  );
};
